import React, { useState, useEffect } from "react";
import { Trash2, Gauge, Hash, Tag, Pencil, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown";

const fmt = (n) => (n == null ? "-" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtInt = (n) => (n == null ? "-" : Number(n).toLocaleString());

const DEFAULT_TERMS = [12, 18, 24, 30, 36, 48, 60, 72, 84, 96, 120];
const DEFAULT_PRODUCTS = ["essentials_powertrain"];

// Stable fallback mapping used for legacy quotes that don't yet embed
// `markup_category`. New quotes carry it directly from the backend.
const FALLBACK_MARKUP_CATEGORY = {
  essentials_powertrain: "vsc",
  essentials_plus: "vsc",
  essentials_enhanced: "vsc",
  essentials_exclusionary: "vsc",
  bundle_4n1: "bundle",
  ancillary_tire_wheel: "bundle",
  ancillary_paintless_dent: "bundle",
  ancillary_key_replacement: "bundle",
  ancillary_windshield: "bundle",
  gap: "gap",
  wear_guard: "wear_guard",
  limited_warranty: "limited_warranty",
  go_forever_lifetime: "go_forever_lifetime",
  go_certified_preowned: "go_certified_preowned",
};

export function displayedQuotes(quotes, markup = 0, selectedTerms = null, selectedProducts = null, categoryMarkups = null, gapSettings = null, vscDealerType = null, surchargeTotal = 0, newcarSurchargeTotal = 0) {
  if (!Array.isArray(quotes)) return [];
  const terms = selectedTerms && selectedTerms.length ? selectedTerms : DEFAULT_TERMS;
  const prods = selectedProducts && selectedProducts.length ? selectedProducts : DEFAULT_PRODUCTS;
  const catMarks = categoryMarkups || {};
  const globalMarkup = Number(markup || 0);
  const gap = gapSettings || {};
  const sur = Number(surchargeTotal || 0);
  return quotes
    .filter((q) => prods.includes(q.product || "essentials_powertrain"))
    .filter((q) => terms.includes(q.term_months))
    .filter((q) => {
      const p = q.product || "essentials_powertrain";
      if (!vscDealerType) return true;
      if (p.startsWith("essentials_")) return vscDealerType === "independent";
      if (p.startsWith("franchise_")) return vscDealerType === "franchise";
      return true;
    })
    .filter((q) => {
      // GAP quotes: only show ones matching proposal's dealer_type + LTV.
      if (q.product !== "gap") return true;
      if (!gap.dealer_type || q.dealer_type !== gap.dealer_type) return false;
      if (gap.dealer_type === "ford_flex") return true; // no LTV
      return q.ltv === gap.ltv;
    })
    .map((q) => {
      const cat = q.markup_category || FALLBACK_MARKUP_CATEGORY[q.product] || "vsc";
      const catMark = catMarks[cat];
      const eff = catMark != null && catMark !== "" ? Number(catMark) : globalMarkup;
      const prodKey = String(q.product || "");
      const base = Number(q.base_price ?? 0) + (prodKey.startsWith("blended_") ? sur : 0) + (prodKey.startsWith("newcar_") ? Number(newcarSurchargeTotal || 0) : 0);
      const total = base + (isFinite(eff) ? eff : 0);
      return { ...q, markup_category: cat, total_price: total };
    });
}

function groupByProduct(quotes) {
  const groups = {};
  for (const q of quotes) {
    const key = q.product || "essentials_powertrain";
    if (!groups[key]) groups[key] = { label: q.product_label || "Essentials VSC", items: [] };
    groups[key].items.push(q);
  }
  return groups;
}

export function VehicleQuoteBlock({ vq, index, onRemove, markup = 0, selectedTerms = null, selectedProducts = null, categoryMarkups = null, gapSettings = null, vscDealerType = null, proposalId = null, onVehicleUpdated = null, editable = true }) {
  const v = vq.vehicle || {};
  const prods = selectedProducts && selectedProducts.length ? selectedProducts : DEFAULT_PRODUCTS;
  const hasBlended = prods.some((k) => String(k).startsWith("blended_"));
  const hasNewcar = prods.some((k) => String(k).startsWith("newcar_"));
  const programOn = (s) => (s.applies_to === "newcar" ? hasNewcar : hasBlended);
  const vehicleSurcharges = (vq.surcharges || []).filter(programOn);
  const surchargeTotal = vehicleSurcharges.filter((x) => x.applies_to !== "newcar").reduce((s, x) => s + Number(x.amount || 0), 0);
  const newcarSurchargeTotal = vehicleSurcharges.filter((x) => x.applies_to === "newcar").reduce((s, x) => s + Number(x.amount || 0), 0);
  const quotes = displayedQuotes(vq.quotes || [], markup, selectedTerms, selectedProducts, categoryMarkups, gapSettings, vscDealerType, surchargeTotal, newcarSurchargeTotal);
  const groups = groupByProduct(quotes);
  const title = `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || v.title || "Vehicle";

  const [catalog, setCatalog] = useState([]);
  const [savingSur, setSavingSur] = useState(false);
  useEffect(() => {
    if (!editable || !proposalId) return;
    api.get("/rates/surcharges").then(({ data }) => setCatalog(data.surcharges || [])).catch(() => {});
  }, [editable, proposalId]);
  // Only offer surcharges that belong to a program currently selected on the proposal.
  const availableSurcharges = catalog.filter(programOn);
  const toggleSurcharge = async (key) => {
    const cur = (vq.surcharges || []).map((s) => s.key);
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    setSavingSur(true);
    try {
      const { data } = await api.patch(`/proposals/${proposalId}/vehicles/${index}`, { surcharges: next });
      onVehicleUpdated?.(data);
    } catch (e) {
      toast.error("Failed to update surcharges");
    } finally {
      setSavingSur(false);
    }
  };

  // Inline mileage editor state
  const [editingMi, setEditingMi] = useState(false);
  const [miDraft, setMiDraft] = useState(v.mileage ?? "");
  const [savingMi, setSavingMi] = useState(false);

  const startEditMi = () => {
    setMiDraft(v.mileage ?? "");
    setEditingMi(true);
  };
  const cancelEditMi = () => {
    setEditingMi(false);
    setMiDraft(v.mileage ?? "");
  };
  const saveMi = async () => {
    const n = Number(String(miDraft).replace(/[^\d]/g, ""));
    if (!Number.isFinite(n) || n < 0 || n > 999999) {
      toast.error("Enter a valid mileage (0–999,999)");
      return;
    }
    if (!proposalId) {
      toast.error("Cannot save — missing proposal id");
      return;
    }
    setSavingMi(true);
    try {
      const { data } = await api.patch(`/proposals/${proposalId}/vehicles/${index}`, { mileage: n });
      onVehicleUpdated?.(data);
      toast.success("Mileage updated & quotes re-run");
      setEditingMi(false);
    } catch (e) {
      toast.error("Failed to update mileage");
    } finally {
      setSavingMi(false);
    }
  };

  return (
    <Card
      className="bg-white border border-[#E9ECEF] rounded-sm overflow-hidden"
      data-testid={`vehicle-block-${index}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr]">
        {/* Image + info (compact) */}
        <div className="p-4 border-b lg:border-b-0 lg:border-r border-[#E9ECEF] bg-[#F8F9FA]">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">
              Vehicle {String(index + 1).padStart(2, "0")}
            </div>
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm text-[10px] h-6 px-2 no-print"
                data-testid={`remove-vehicle-${index}`}
              >
                <Trash2 size={11} />
              </Button>
            )}
          </div>
          <div className="aspect-[16/10] rounded-sm overflow-hidden bg-[#0B132B] mb-3 border border-[#E9ECEF]">
            {v.image_url ? (
              <img
                src={v.image_url}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid={`vehicle-image-${index}`}
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-[#D4AF37] text-[10px] uppercase tracking-[0.24em]">
                No image
              </div>
            )}
          </div>
          <h3
            className="font-display text-base font-black text-[#0B132B] leading-tight tracking-tight"
            data-testid={`vehicle-title-${index}`}
          >
            {title}
          </h3>
          {v.trim && <p className="text-xs text-[#6C757D] mt-0.5 truncate">{v.trim}</p>}
          {/* Prominent mileage line — inline editable */}
          <div className="mt-2" data-testid={`vehicle-mileage-${index}`}>
            {editingMi ? (
              <div className="flex items-center gap-1.5 no-print">
                <Gauge size={12} className="text-[#D4AF37]" />
                <Input
                  type="text"
                  inputMode="numeric"
                  value={miDraft}
                  onChange={(e) => setMiDraft(e.target.value.replace(/[^\d,]/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveMi();
                    if (e.key === "Escape") cancelEditMi();
                  }}
                  autoFocus
                  className="h-7 w-24 text-sm font-mono rounded-sm"
                  placeholder="Miles"
                  data-testid={`mileage-input-${index}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={saveMi}
                  disabled={savingMi}
                  className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50"
                  data-testid={`mileage-save-${index}`}
                >
                  {savingMi ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEditMi}
                  disabled={savingMi}
                  className="h-7 w-7 p-0 text-[#6C757D] hover:bg-[#E9ECEF]"
                  data-testid={`mileage-cancel-${index}`}
                >
                  <X size={13} />
                </Button>
              </div>
            ) : (
              <div className="flex items-baseline gap-1.5">
                <Gauge size={12} className="text-[#D4AF37]" />
                <span className="font-display text-base font-black text-[#0B132B] tracking-tight">
                  {v.mileage != null ? fmtInt(v.mileage) : "—"}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6C757D]">
                  {"\u00A0"}current miles
                </span>
                {editable && proposalId && (
                  <button
                    type="button"
                    onClick={startEditMi}
                    className="ml-1 text-[#6C757D] hover:text-[#0B132B] no-print"
                    title="Edit mileage"
                    data-testid={`mileage-edit-${index}`}
                  >
                    <Pencil size={11} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
            {v.price != null && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-white border border-[#E9ECEF] text-[#0B132B]">
                <Tag size={10} /> {fmt(v.price)}
              </span>
            )}
            {v.vin && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-white border border-[#E9ECEF] text-[#0B132B] font-mono">
                <Hash size={10} /> {v.vin.slice(-6)}
              </span>
            )}
          </div>
          {(availableSurcharges.length > 0 || vehicleSurcharges.length > 0) && (
            <div className="mt-3" data-testid={`vehicle-surcharges-${index}`}>
              {editable && proposalId && availableSurcharges.length > 0 && (
                <div className="no-print">
                  <MultiSelectDropdown
                    label="Surcharges"
                    testId={`surcharge-picker-${index}`}
                    placeholder="None applied"
                    disabled={savingSur}
                    options={availableSurcharges.map((s) => ({ key: s.key, label: `${s.label} +${fmt(s.amount)}`, hint: s.applies_to === "newcar" ? "Essentials New Car" : "Blended VSC" }))}
                    selected={vehicleSurcharges.map((s) => s.key)}
                    onToggle={(key) => toggleSurcharge(key)}
                    renderSummary={(picked) => picked.length === 0 ? "None applied" : `${picked.length} applied · +${fmt(surchargeTotal + newcarSurchargeTotal)}`}
                  />
                </div>
              )}
              {vehicleSurcharges.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {vehicleSurcharges.map((s) => (
                    <span key={s.key} className="px-1.5 py-0.5 rounded-sm bg-[#0B132B] text-white text-[10px]" data-testid={`surcharge-toggle-${index}-${s.key}`}>
                      {s.applies_to === "newcar" ? "New Car · " : ""}{s.label} +{fmt(s.amount)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {v.url && !v.url.startsWith("vin:") && (
            <a
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-[10px] text-[#6C757D] hover:text-[#0B132B] break-all no-print"
              data-testid={`vehicle-source-${index}`}
            >
              Source →
            </a>
          )}
        </div>

        {/* Quotes tables (compact, no Monthly column) */}
        <div className="p-4 space-y-4">
          {Object.keys(groups).length === 0 ? (
            <div className="text-xs text-[#6C757D] italic py-6 text-center">
              No eligible warranty options for this vehicle.
            </div>
          ) : (
            Object.entries(groups).map(([productKey, group]) => (
              <div key={productKey} data-testid={`quotes-table-${index}-${productKey}`}>
                <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#D4AF37] mb-1.5">
                  {group.label}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs"
                         data-testid={productKey === "essentials_powertrain" && index === 0 ? `quotes-table-${index}` : undefined}>
                    <thead>
                      <tr className="bg-[#0B132B] text-white text-[9px] uppercase tracking-[0.12em]">
                        <th className="text-left px-2 py-1.5 font-semibold">Term</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Coverage</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Deductible</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((q, i) => {
                        const rowKey = `${q.product}-${q.term_months}-${q.coverage_miles}`;
                        return (
                        <tr
                          key={rowKey}
                          className={`border-b border-[#E9ECEF] ${i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"}`}
                        >
                          <td className="px-2 py-1.5 font-bold text-[#0B132B]">{q.term_months}mo</td>
                          <td className="px-2 py-1.5 text-[#0B132B]">{fmtInt(q.coverage_miles)} mi</td>
                          <td className="px-2 py-1.5 text-right text-[#6C757D]">${q.deductible}</td>
                          <td className="px-2 py-1.5 text-right font-bold text-[#0B132B]">{fmt(q.total_price)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}
